import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../ui";
import { Modal, CloseButton } from "react-bootstrap";
import { MDBInput, MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import { ChevronRight, Eye, EyeOff } from "lucide-react";
import { api } from "../../services";
import { toast } from "react-toastify";
import { normalizeRestaurantId } from "../../utils/utils";
import { formatRoleDisplayName, formatPermissionLabel } from "../../utils/rbacApi";
import { useTranslation } from "react-i18next";
import { pickLatestAssignment, assignmentsAllRestaurantScope, aggregateBranchIdsFromAssignments } from "../../utils/userScope";
import { isValidEmail, isValidKsaPhone, normalizeKsaMobile, formatKsaE164 } from "../../utils/validators";
import { usePermission } from "../../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../../config/routePermissions";
import { ONEHUB_BRAND_FORM_CONTROLS_CSS, ONEHUB_BRAND_HEX } from "../rbac/brandFormControlsCss";
import { MODAL_PAD_X, MODAL_PAD_Y } from "./userManagementModalLayout";

const BRAND = ONEHUB_BRAND_HEX;
const FONT_UI = "Inter, sans-serif";

/** Modal visual tokens aligned with Edit user hi-fi mock */
const C = {
  text: "#212529",
  textMuted: "#6c757d",
  border: "#dee2e6",
  headerRule: "#e9ecef",
  selectAllBg: "#f7f3eb",
  branchPanelBg: "#e9ecef",
  brandRowBorder: "#dee2e6",
  pillBg: "#f0ebe3",
  /** Delete user danger zone — unified #E21D1D accent */
  dangerBorder: "#E21D1D",
  dangerBg: "#ffffff",
  dangerTitle: "#E21D1D",
  dangerDescription: "#E21D1D",
};

/** Flat MDB-outline inputs to thin gray border / 4px radius (matches mock). */
const EDIT_USER_MODAL_INPUT_CSS = `
.edit-user-modal-body .form-outline {
  margin-bottom: 0;
}
.edit-user-modal-body .form-outline .form-notch,
.edit-user-modal-body .form-outline .form-notch-leading,
.edit-user-modal-body .form-outline .form-notch-middle,
.edit-user-modal-body .form-outline .form-notch-trailing {
  display: none !important;
}
.edit-user-modal-body .form-outline .form-control {
  border: 1px solid ${C.border} !important;
  border-radius: 4px !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.9375rem !important;
  background-color: #fff !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
}
.edit-user-modal-body .form-outline .form-control:focus {
  border-color: var(--color-primary, #083B4C) !important;
  box-shadow: 0 0 0 3px rgba(8, 59, 76, 0.16) !important;
}
.edit-user-modal-body h6.edit-user-modal-section-heading-role-brand {
  margin-top: 0 !important;
  margin-bottom: 8px !important;
}
`;

const SECTION_GAP = 32;

const sectionHeadingStyle = {
  fontFamily: FONT_UI,
  fontWeight: 700,
  fontSize: "0.6875rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: C.textMuted,
  marginBottom: 14,
};

/** Role + brand/branch section labels — Figma: Inter 600, 12px/150%, #303030, uppercase. */
const roleBrandSectionHeadingStyle = {
  fontFamily: FONT_UI,
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "150%",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: "#303030",
  flex: "none",
  alignSelf: "flex-start",
};

function splitDisplayName(username) {
  const parts = String(username || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
}

export default function EditUserModal({ show, user, restaurantId, onHide, onSaved, onRequestDelete }) {
  const { t, i18n } = useTranslation("translation");
  const isRtl = i18n.dir() === "rtl";
  const { bypass, can } = usePermission();
  const canEditUser = can(RBAC_MODULES.USERS, PERMISSION_ACTIONS.EDIT);
  const canDeleteUser = can(RBAC_MODULES.USERS, PERMISSION_ACTIONS.DELETE);
  const noPermissionTip = t("noPermissionToAccessThis");
  const rid = normalizeRestaurantId(restaurantId);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [fullBranchAccess, setFullBranchAccess] = useState(true);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [expandedStores, setExpandedStores] = useState(() => new Set());
  const [expandedRoleDetailIds, setExpandedRoleDetailIds] = useState(() => new Set());

  const [saving, setSaving] = useState(false);

  const hydrateFromUser = useCallback((u, storeList) => {
    if (!u) return;
    const storedFn = String(u.firstName ?? "").trim();
    const storedLn = String(u.lastName ?? "").trim();
    const fromUsername = splitDisplayName(u.username);
    setFirstName(storedFn || fromUsername.firstName);
    setLastName(storedLn || fromUsername.lastName);
    setUsername(u.username || "");
    setEmail(u.email || "");
    setPhone(u.phone || "");
    setPassword("");
    setShowPass(false);
    const assignments = u.assignments || [];
    const latest = pickLatestAssignment(assignments);
    const ridRole = latest?.roleId;
    setSelectedRoleId(ridRole?._id ? String(ridRole._id) : "");

    const allRestaurantScope = assignmentsAllRestaurantScope(assignments);
    setFullBranchAccess(allRestaurantScope);
    setSelectedBranchIds(aggregateBranchIdsFromAssignments(assignments, storeList || []));
  }, []);

  useEffect(() => {
    if (!show || !user) return;

    setExpandedRoleDetailIds(new Set());

    setLoadingRoles(true);
    api
      .get("/v1/roles")
      .then((res) => {
        const raw = res.data;
        setRoles(Array.isArray(raw) ? raw : raw?.data || []);
      })
      .catch(() => setRoles([]))
      .finally(() => setLoadingRoles(false));

    setLoadingStores(true);
    api
      .get("/stores/my")
      .then((res) => {
        const list = res.data.stores || [];
        setStores(list);
        hydrateFromUser(user, list);

        const assignments = user.assignments || [];
        const impliedBranchIds = aggregateBranchIdsFromAssignments(assignments, list);
        const nextExpanded = new Set();
        for (const store of list) {
          const branches = store.branches || [];
          if (branches.length === 0) continue;
          if (branches.some((b) => impliedBranchIds.includes(String(b._id)))) {
            nextExpanded.add(store._id);
          }
        }
        setExpandedStores(nextExpanded);
      })
      .catch(() => {
        setStores([]);
        hydrateFromUser(user, []);
        setExpandedStores(new Set());
      })
      .finally(() => setLoadingStores(false));
  }, [show, user?._id, hydrateFromUser]);

  function toggleBranch(branchId) {
    const id = String(branchId);
    if (fullBranchAccess) setFullBranchAccess(false);
    setSelectedBranchIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleAllBranchesInStore(store) {
    if (fullBranchAccess) setFullBranchAccess(false);
    const bids = (store.branches || []).map((b) => String(b._id));
    const allOn = bids.length > 0 && bids.every((id) => selectedBranchIds.includes(id));
    setSelectedBranchIds((prev) => {
      if (allOn) return prev.filter((id) => !bids.includes(id));
      const set = new Set(prev);
      bids.forEach((id) => set.add(id));
      return [...set];
    });
  }

  function toggleStoreExpandedRow(storeId) {
    setExpandedStores((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  }

  function toggleRolePermissionsExpanded(roleId) {
    const id = String(roleId);
    setExpandedRoleDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    if (!canEditUser) {
      toast.error(noPermissionTip);
      return;
    }
    if (!user?._id) return;
    const uname = username.trim() || `${firstName} ${lastName}`.trim();
    if (!uname) {
      toast.warn(t("editUserModal.usernameRequired"));
      return;
    }
    if (!selectedRoleId) {
      toast.warn(t("editUserModal.roleRequired"));
      return;
    }

    const phoneTrimmed = phone.trim();
    if (phoneTrimmed && !isValidKsaPhone(phoneTrimmed)) {
      toast.error(t("addUserWizard.invalidKsaPhone"));
      return;
    }
    const emailTrim = email.trim();
    if (emailTrim && !isValidEmail(emailTrim)) {
      toast.error(t("addUserWizard.invalidEmail"));
      return;
    }

    setSaving(true);
    try {
      const phonePayload = phoneTrimmed ? formatKsaE164(normalizeKsaMobile(phoneTrimmed)) : "";
      const body = {
        username: uname,
        phone: phonePayload,
        email: emailTrim ? emailTrim.toLowerCase() : "",
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };
      if (password.trim()) body.password = password.trim();
      await api.put(`/v1/staff-users/${user._id}`, body);

      const storesForAssignment = [];
      if (bypass && stores.length > 0) {
        if (fullBranchAccess) {
          storesForAssignment.push(...stores.map((store) => store._id));
        } else if (selectedBranchIds.length > 0) {
          const uniqueStoreIds = new Set();
          stores.forEach((store) => {
            if (store.branches?.some((branch) => selectedBranchIds.includes(String(branch._id)))) {
              uniqueStoreIds.add(store._id);
            }
          });
          storesForAssignment.push(...Array.from(uniqueStoreIds));
        } else {
          storesForAssignment.push(...stores.map((store) => store._id));
        }
      } else if (rid) {
        storesForAssignment.push(rid);
      }

      if (storesForAssignment.length === 0) {
        toast.error(t("rbac.restaurantContextRequiredForAssignments"));
        return;
      }

      const assignments = [];
      for (const storeId of storesForAssignment) {
        let storeScope;
        if (fullBranchAccess) {
          storeScope = { scopeType: "restaurant", scopeIds: [] };
        } else {
          const store = stores.find((s) => String(s._id) === String(storeId));
          const storeBranchIds =
            store?.branches
              ?.filter((branch) => selectedBranchIds.includes(String(branch._id)))
              .map((branch) => branch._id) || [];

          if (
            storeBranchIds.length === (store?.branches?.length || 0) &&
            storeBranchIds.length > 0
          ) {
            storeScope = { scopeType: "store", scopeIds: [storeId] };
          } else {
            storeScope = { scopeType: "branch", scopeIds: storeBranchIds };
          }
        }
        assignments.push({
          restaurantId: storeId,
          scopeType: storeScope.scopeType,
          scopeIds: storeScope.scopeIds,
        });
      }

      await api.patch(`/v1/staff-users/${user._id}/access`, {
        roleId: selectedRoleId,
        assignments,
      });

      toast.success(t("editUserModal.userUpdated"));
      onSaved?.();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || t("editUserModal.updateFailed"));
    } finally {
      setSaving(false);
    }
  }

  const branchStepSummary = useMemo(() => {
    const brandCount = stores.length;
    const totalBranches = stores.reduce((acc, s) => acc + (s.branches?.length || 0), 0);
    if (fullBranchAccess) {
      return t("addUserWizard.branchSummaryAcross", {
        branches: totalBranches,
        brands: brandCount,
      });
    }
    const sel = new Set((selectedBranchIds || []).map(String));
    let br = 0;
    const brandsWithSelection = new Set();
    for (const store of stores) {
      for (const b of store.branches || []) {
        if (sel.has(String(b._id))) {
          br += 1;
          brandsWithSelection.add(String(store._id));
        }
      }
    }
    return t("addUserWizard.branchSummaryAcross", {
      branches: br,
      brands: brandsWithSelection.size,
    });
  }, [stores, fullBranchAccess, selectedBranchIds, t]);

  if (!show || !user) return null;

  return (
    <Modal
      show={show}
      onHide={() => onHide()}
      size="lg"
      centered
      scrollable
      backdrop="static"
      lang={i18n.language}
      contentClassName={isRtl ? "edit-user-modal-rtl" : undefined}
    >
      <Modal.Header
        closeButton={false}
        className="rounded-0"
        style={{
          fontFamily: FONT_UI,
          padding: `${MODAL_PAD_Y}px ${MODAL_PAD_X}px ${MODAL_PAD_Y}px ${MODAL_PAD_X}px`,
          borderTop: "none",
          borderInline: "none",
          borderBottom: `1px solid ${C.headerRule}`,
        }}
      >
        <div className="w-100">
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div className="min-w-0">
              <Modal.Title
                className="mb-0"
                style={{ fontFamily: FONT_UI, fontWeight: 700, fontSize: "1.125rem", color: C.text, lineHeight: 1.3 }}
              >
                {t("editUserModal.title")}
              </Modal.Title>
              <div style={{ fontSize: "0.8125rem", color: C.textMuted, marginTop: 4 }}>{t("editUserModal.subtitle")}</div>
            </div>
            <CloseButton
              className="flex-shrink-0 m-0"
              style={{ fontSize: "0.75rem", opacity: 0.55 }}
              aria-label={t("close")}
              onClick={() => onHide()}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body
        className="onehub-brand-form-controls edit-user-modal-body"
        style={{ padding: `${MODAL_PAD_Y}px ${MODAL_PAD_X}px`, fontFamily: FONT_UI }}
      >
        <style>{ONEHUB_BRAND_FORM_CONTROLS_CSS}</style>
        <style>{EDIT_USER_MODAL_INPUT_CSS}</style>
        <h6 className="mb-0" style={sectionHeadingStyle}>
          {t("editUserModal.sectionUserInfo")}
        </h6>
        <div className="row g-3" style={{ marginBottom: SECTION_GAP }}>
          <div className="col-md-6">
            <label style={lbl}>{t("First name")}</label>
            <MDBInput value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label style={lbl}>{t("Last name")}</label>
            <MDBInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className="col-12">
            <label style={lbl}>{t("Username")}</label>
            <MDBInput value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div className="col-12">
            <label style={lbl}>{t("Password")}</label>
            <div style={{ position: "relative" }}>
              <MDBInput
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("editUserModal.passwordHint")}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                aria-label={showPass ? t("userManagementPage.hidePassword") : t("userManagementPage.showPassword")}
                style={{
                  position: "absolute",
                  insetInlineEnd: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                {showPass ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
              </button>
            </div>
          </div>
          <div className="col-12">
            <label style={lbl}>{t("Phone number")}</label>
            <MDBInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("editUserModal.phonePlaceholder")}
            />
          </div>
          <div className="col-12">
            <label style={lbl}>
              {t("Email")} ({t("rbac.optionalLower")})
            </label>
            <MDBInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("editUserModal.emailPlaceholder")}
            />
          </div>
        </div>

        <h6 className="edit-user-modal-section-heading-role-brand" style={roleBrandSectionHeadingStyle}>
          {t("editUserModal.sectionRole")}
        </h6>
        {loadingRoles ? (
          <div className="text-center py-4" style={{ marginBottom: SECTION_GAP }}>
            <MDBSpinner />
          </div>
        ) : (
          <div className="d-flex flex-column gap-2" style={{ marginBottom: SECTION_GAP }}>
            {roles.map((r) => {
              const rid = String(r._id);
              const selected = selectedRoleId === rid;
              const expanded = expandedRoleDetailIds.has(rid);
              const perms = r.permissions || [];
              const showExpandControl = perms.length > 0;
              const roleLabel = formatRoleDisplayName(r.name);
              const pillStyle = {
                fontSize: "0.6875rem",
                fontWeight: 500,
                color: C.text,
                background: C.pillBg,
                borderRadius: 4,
                padding: "4px 8px",
                lineHeight: 1.2,
              };
              return (
                <div
                  key={r._id}
                  style={{
                    ...roleCard,
                    cursor: "default",
                    flexDirection: "column",
                    alignItems: "stretch",
                    borderWidth: 2,
                    borderStyle: "solid",
                    borderColor: selected ? BRAND : C.border,
                    background: "#fff",
                  }}
                >
                  <div className="d-flex align-items-start gap-2 w-100">
                    <input
                      type="radio"
                      id={`edit-user-role-${rid}`}
                      name="edit-role"
                      checked={selected}
                      onChange={() => setSelectedRoleId(rid)}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: BRAND,
                        cursor: "pointer",
                        margin: "5px 0 0 0",
                        flexShrink: 0,
                      }}
                    />
                    <label
                      htmlFor={`edit-user-role-${rid}`}
                      className="flex-grow-1 min-w-0 mb-0"
                      style={{ cursor: "pointer", paddingTop: 2, paddingBottom: 2 }}
                    >
                      <div className="fw-bold" style={{ fontSize: "0.9375rem", color: C.text }}>
                        {roleLabel}
                      </div>
                      {r.description && (
                        <div style={{ fontSize: "0.8125rem", color: C.textMuted, marginTop: 2, lineHeight: 1.35 }}>
                          {r.description}
                        </div>
                      )}
                      {!expanded && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {perms.slice(0, 6).map((p) => (
                            <span key={p} style={pillStyle}>
                              {formatPermissionLabel(p)}
                            </span>
                          ))}
                          {perms.length > 6 && (
                            <span className="small text-muted align-self-center">+{perms.length - 6}</span>
                          )}
                        </div>
                      )}
                    </label>
                    {showExpandControl ? (
                      <button
                        type="button"
                        className="flex-shrink-0 align-self-start border-0 bg-transparent d-inline-flex align-items-center justify-content-center p-0 m-0"
                        style={{
                          lineHeight: 0,
                          color: C.textMuted,
                          opacity: 0.7,
                          cursor: "pointer",
                          minWidth: 28,
                          minHeight: 28,
                          marginTop: 2,
                        }}
                        aria-expanded={expanded}
                        aria-label={
                          expanded
                            ? t("addUserWizard.collapseRolePermissionsAria", { role: roleLabel })
                            : t("addUserWizard.expandRolePermissionsAria", { role: roleLabel })
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleRolePermissionsExpanded(r._id);
                        }}
                      >
                        <ChevronRight
                          size={18}
                          strokeWidth={2}
                          style={{
                            transform: (() => {
                              const parts = [];
                              if (expanded) parts.push("rotate(90deg)");
                              if (isRtl) parts.push("scaleX(-1)");
                              return parts.length ? parts.join(" ") : "none";
                            })(),
                            transition: "transform 0.2s ease",
                          }}
                          aria-hidden
                        />
                      </button>
                    ) : null}
                  </div>
                  {expanded && perms.length > 0 ? (
                    <div
                      className="d-flex flex-wrap gap-1 w-100"
                      style={{
                        marginTop: 10,
                        paddingInlineStart: 32,
                        boxSizing: "border-box",
                      }}
                    >
                      {perms.map((p) => (
                        <span key={p} style={pillStyle}>
                          {formatPermissionLabel(p)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <h6 className="edit-user-modal-section-heading-role-brand" style={roleBrandSectionHeadingStyle}>
          {t("addUserWizard.selectBrandBranchTitle")}
        </h6>

        <div
          className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 rounded-2"
          style={{
            background: C.selectAllBg,
            border: `1px solid ${C.brandRowBorder}`,
            fontFamily: FONT_UI,
            marginBottom: 12,
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-0">
            <input
              type="checkbox"
              id="edit-select-all-brands-branches"
              checked={fullBranchAccess}
              onChange={(e) => {
                const checked = e.target.checked;
                setFullBranchAccess(checked);
                if (checked) {
                  setSelectedBranchIds([]);
                }
              }}
              style={{
                width: 16,
                height: 16,
                accentColor: BRAND,
                cursor: "pointer",
                margin: 0,
              }}
            />
            <label
              htmlFor="edit-select-all-brands-branches"
              className="mb-0 text-dark"
              style={{
                cursor: "pointer",
                fontFamily: FONT_UI,
                fontSize: "0.8125rem",
                fontWeight: 600,
                lineHeight: 1.2,
                display: "flex",
                alignItems: "center",
              }}
            >
              {t("addUserWizard.selectAllBrandsBranches")}
            </label>
          </div>
          <span className="text-dark text-nowrap fw-bold" style={{ fontSize: "0.8125rem" }}>
            {branchStepSummary}
          </span>
        </div>

        {loadingStores ? (
          <div className="text-center py-5" style={{ marginBottom: SECTION_GAP }}>
            <MDBSpinner />
          </div>
        ) : fullBranchAccess ? (
          <p className="text-muted small px-1" style={{ fontFamily: FONT_UI, marginBottom: SECTION_GAP }}>
            {t("addUserWizard.fullAccessBranchesHint")}
          </p>
        ) : (
          <div style={{ marginBottom: SECTION_GAP }}>
            {stores.map((store) => {
              const branches = store.branches || [];
              const expanded = expandedStores.has(store._id);
              const bids = branches.map((b) => String(b._id));
              const numSel = bids.filter((id) => selectedBranchIds.includes(id)).length;
              const allSelected = branches.length > 0 && numSel === branches.length;
              const someSelected = numSel > 0 && numSel < branches.length;

              return (
                <div
                  key={store._id}
                  className="mb-2"
                  style={{
                    border: `1px solid ${C.brandRowBorder}`,
                    borderRadius: 8,
                    background: "#fff",
                    overflow: "hidden",
                    fontFamily: FONT_UI,
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    role={branches.length > 0 ? "button" : undefined}
                    tabIndex={branches.length > 0 ? 0 : undefined}
                    aria-expanded={branches.length > 0 ? expanded : undefined}
                    aria-label={
                      branches.length > 0
                        ? `${store.name || ""}: ${
                            expanded
                              ? t("addUserWizard.collapseBranchList")
                              : t("addUserWizard.expandBranchList")
                          }`
                        : undefined
                    }
                    onClick={(e) => {
                      if (!branches.length) return;
                      const el = e.target;
                      if (typeof el.closest === "function" && el.closest(".form-check")) return;
                      toggleStoreExpandedRow(store._id);
                    }}
                    onKeyDown={(e) => {
                      if (!branches.length) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleStoreExpandedRow(store._id);
                      }
                    }}
                    style={{
                      cursor: branches.length > 0 ? "pointer" : undefined,
                      fontFamily: FONT_UI,
                    }}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: 16, flexBasis: 16 }}
                    >
                      <input
                        type="checkbox"
                        id={`edit-store-all-${store._id}`}
                        className="m-0 flex-shrink-0"
                        checked={branches.length > 0 && allSelected}
                        ref={(el) => {
                          if (el && branches.length) el.indeterminate = someSelected;
                        }}
                        onChange={() => toggleAllBranchesInStore(store)}
                        disabled={branches.length === 0}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${t("rbac.selectAllBranches")}: ${store.name || ""}`}
                        style={{
                          width: 16,
                          height: 16,
                          accentColor: BRAND,
                          cursor: branches.length === 0 ? "not-allowed" : "pointer",
                          margin: 0,
                          opacity: branches.length === 0 ? 0.6 : 1,
                        }}
                      />
                    </span>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-bold text-dark" style={{ fontSize: 14 }}>
                        {store.name}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 1, lineHeight: 1.3 }}>
                        {t("addUserWizard.branchCountInline", { count: branches.length })}
                      </div>
                    </div>
                    {branches.length > 0 ? (
                      <span
                        className="flex-shrink-0 text-dark align-self-center d-inline-flex align-items-center"
                        style={{ lineHeight: 0, opacity: 0.75 }}
                        aria-hidden
                      >
                        <ChevronRight
                          size={18}
                          strokeWidth={2}
                          style={{
                            transform: (() => {
                              const parts = [];
                              if (expanded) parts.push("rotate(90deg)");
                              if (isRtl) parts.push("scaleX(-1)");
                              return parts.length ? parts.join(" ") : "none";
                            })(),
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </span>
                    ) : null}
                  </div>
                  {expanded && branches.length > 0 ? (
                    <div
                      className="px-3"
                      style={{
                        background: C.branchPanelBg,
                        borderTop: `1px solid ${C.brandRowBorder}`,
                        paddingTop: 10,
                        paddingBottom: 12,
                      }}
                    >
                      {branches.map((b) => (
                        <div key={b._id} className="d-flex align-items-center gap-2 mb-1 ps-3">
                          <span
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 16, flexBasis: 16 }}
                          >
                            <input
                              type="checkbox"
                              id={`edit-branch-${b._id}`}
                              checked={selectedBranchIds.includes(String(b._id))}
                              onChange={() => toggleBranch(b._id)}
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: BRAND,
                                cursor: "pointer",
                                margin: 0,
                              }}
                            />
                          </span>
                          <label
                            htmlFor={`edit-branch-${b._id}`}
                            className="mb-0"
                            style={{
                              cursor: "pointer",
                              fontFamily: FONT_UI,
                              fontSize: 13,
                              lineHeight: 1.2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {b.name || String(b._id)}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <div
          className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-0"
          style={{
            padding: "18px 20px",
            borderRadius: 2,
            border: `1px solid ${C.dangerBorder}`,
            background: C.dangerBg,
          }}
        >
          <div className="min-w-0">
            <div className="fw-bold" style={{ color: C.dangerTitle, fontSize: "0.9375rem", lineHeight: 1.3 }}>
              {t("editUserModal.deleteHeading")}
            </div>
            <div
              style={{
                color: C.dangerDescription,
                fontSize: "0.8125rem",
                fontWeight: 400,
                marginTop: 6,
                lineHeight: 1.35,
              }}
            >
              {t("editUserModal.deleteDescription")}
            </div>
          </div>
          {canDeleteUser ? (
            <Button
              type="button"
              outline
              color="danger"
              size="sm"
              className="align-self-stretch align-self-md-center flex-shrink-0"
              onClick={() => {
                onRequestDelete?.(user);
              }}
            >
              {t("editUserModal.deleteButton")}
            </Button>
          ) : (
            <MDBTooltip tag="span" title={noPermissionTip}>
              <span className="d-inline-block">
                <Button
                  type="button"
                  outline
                  color="danger"
                  size="sm"
                  className="align-self-stretch align-self-md-center flex-shrink-0"
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  {t("editUserModal.deleteButton")}
                </Button>
              </span>
            </MDBTooltip>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        className="d-flex justify-content-between align-items-center w-100 flex-nowrap gap-3 rounded-0"
        style={{
          fontFamily: FONT_UI,
          borderInline: "none",
          borderBottom: "none",
          borderTop: `1px solid ${C.headerRule}`,
          padding: `${MODAL_PAD_Y}px ${MODAL_PAD_X}px`,
        }}
      >
        <Button
          type="button"
          outline
          color="primary"
          className="m-0"
          onClick={() => onHide()}
          disabled={saving}
        >
          {t("Cancel")}
        </Button>
        <Button
          color="primary"
          className="m-0"
          onClick={handleSave}
          disabled={saving || !canEditUser}
        >
          {saving ? <MDBSpinner size="sm" color="light" /> : t("editUserModal.saveChanges")}
        </Button>
      </Modal.Footer>
      <style>{`
        .edit-user-modal-rtl {
          direction: rtl;
        }
        .edit-user-delete-btn,
        .edit-user-delete-btn:hover,
        .edit-user-delete-btn:focus,
        .edit-user-delete-btn:focus-visible,
        .edit-user-delete-btn:active {
          box-shadow: none !important;
          text-transform: none !important;
        }
        .edit-user-delete-btn:hover {
          background: rgba(226, 29, 29, 0.08) !important;
          color: #c41919 !important;
          border-color: #c41919 !important;
        }
        .edit-user-delete-btn:focus-visible {
          outline: 2px solid rgba(226, 29, 29, 0.45);
          outline-offset: 2px;
        }
      `}</style>
    </Modal>
  );
}

const lbl = { fontSize: "0.75rem", fontWeight: 600, color: C.text, marginBottom: 6, display: "block" };
const roleCard = {
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  padding: "14px 16px",
  borderRadius: 6,
  cursor: "pointer",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};
